/**
 * Content for the templated Focus Area and Innovation pages. Each entry drives a
 * route; the shared page templates read everything they render from here, so a
 * new focus area or product only needs a record added below plus a nav link.
 */

import { PRODUCT_URLS, resolveProductUrl } from '@podus/utils';

export interface FocusAreaPage {
    slug: string;
    /** Greyed lead-in of the hero title. */
    titleLead: string;
    /** Bold remainder of the hero title. */
    titleBold: string;
    /** Optional small line under the title (e.g. the SGBV expansion). */
    subtitle?: string;
    intro: string;
    /** Closing statement, dark first half. */
    statementDark: string;
    /** Closing statement, grey remainder. */
    statementGrey: string;
    /** Hero background from the CMS; the bundled one stands in until an area has its own. */
    heroImage?: string;
}

/**
 * The programme pages, as the CMS publishes them. `mergeFocusAreas` adds each
 * area the feed carries and drops any it no longer does.
 */
export const FOCUS_AREA_PAGES: FocusAreaPage[] = [];

export const getFocusArea = (slug?: string): FocusAreaPage | undefined => FOCUS_AREA_PAGES.find((p) => p.slug === slug);

export interface InnovationPage {
    slug: string;
    /** Greyed lead-in of the hero title. */
    titleLead: string;
    /** Bold remainder of the hero title. */
    titleBold: string;
    /** Name used in the "About Citizens' Gavel …" band. */
    label: string;
    intro: string;
    /** Product mark, from `@podus/ui/logo-icons`. Shown in the page hero. */
    logo: string;
}

/**
 * One entry per product page. Like the focus areas, the CMS owns this list
 * once it has answered: a Live product has a page, a draft does not.
 */
/**
 * Where each product actually lives, for the "Ready to try …" button on its page.
 *
 * Read at call time rather than baked into a constant: `PRODUCT_URLS` resolves
 * per environment through lazy getters, so a module-level copy would freeze
 * whatever the first import happened to see.
 *
 * Checked 2026-09-23 — every URL below answers 200. Two products have no public
 * app of their own yet (Justice Clock, Judicial Accountability); their button
 * goes to the contact page rather than nowhere.
 */
export function innovationSite(slug: string): string {
    switch (slug) {
        case 'podus-ai':
            return PRODUCT_URLS.podusai;
        case 'podus-contract':
            return PRODUCT_URLS.poduscontract;
        case 'podus-appointment':
            return PRODUCT_URLS.podusappointment;
        case 'podus-legal-aid':
            // `PRODUCT_URLS.podusaid` is the signed-in dashboard, which would send
            // a visitor who has never heard of Podus Aid to a login screen. This
            // button is an introduction, so it opens the product's front page.
            return resolveProductUrl('https://app.podus.org');
        case 'podus-checker':
            return PRODUCT_URLS.poduschecker;
        case 'podus-multidoor-court':
            // `multi-door.podus.org`, which the projects data long carried, no
            // longer resolves; the live host is the one in the product map.
            return PRODUCT_URLS.podusmultidoor;
        // Gavel's own tools — not part of the Podus product map.
        case 'iris':
            return 'https://iris.citizensgavel.org';
        case 'harmlist':
            return 'https://harmlist.com';
        case 'volunteer':
            return '/about#volunteers';
        default:
            return '/help';
    }
}

/**
 * The product pages, as the CMS publishes them. `mergeInnovations` replaces this
 * list outright with what the feed carries.
 */
export const INNOVATION_PAGES: InnovationPage[] = [];

export const getInnovation = (slug?: string): InnovationPage | undefined =>
    INNOVATION_PAGES.find((p) => p.slug === slug);
